import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Divider
} from '@mui/material';
import { 
  BeachAccess, 
  AccessTime, 
  ArrowForward as ArrowRight,
  CalendarMonth as Calendar,
  Person as User,
  Description as FileText
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import { Personnel } from '../../types/Personnel';
import { Performance } from '../../types/mongodb';
import UpcomingPerformances from '../dashboard/UpcomingPerformances';
import MyLeaveAndTILContent from './MyLeaveAndTILContent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PersonnelDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [data, setData] = useState<{
    personnel: Personnel | null;
    nextPerformances: Performance[];
    leaveBalance: any;
  }>({
    personnel: null,
    nextPerformances: [],
    leaveBalance: null
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !isAuthenticated) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      if (!user._id) {
        setError('User ID not available');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Fetching data for user:', user._id);

        const personnelRes = await axiosInstance.get(`/api/personnel/by-user/${user._id}`);
        const personnel = personnelRes.data;

        // Fetch performances with populated event and venue
        const performancesRes = await axiosInstance.get(`/api/performances/staff/${personnel._id}`, {
          params: { limit: 5, populate: 'event venue' }
        });

        // Fetch leave balance
        let leaveBalance = null;
        try {
          const leaveRes = await axiosInstance.get(`/api/personnel/${personnel._id}/leave/balance`);
          leaveBalance = leaveRes.data;
        } catch (err) {
          console.log('No leave data available');
        }

        console.log('✅ Performances fetched:', performancesRes.data);

        setData({
          personnel: personnel,
          nextPerformances: performancesRes.data?.data || performancesRes.data || [],
          leaveBalance: leaveBalance
        });
        setLoading(false);
      } catch (err: any) {
        console.error('❌ Error fetching data:', err);
        
        if (err.response?.status === 400) {
          setError('Invalid user ID. Please try logging out and back in.');
        } else if (err.response?.status === 404) {
          setError('Personnel record not found. Please contact admin.');
        } else {
          setError('Failed to load dashboard data');
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isAuthenticated]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 'calc(100vh - 64px)' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { personnel, nextPerformances, leaveBalance } = data;

  if (!personnel) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">No personnel data found</Alert>
      </Box>
    );
  }

  // Extract leave data
  const isStaff = leaveBalance?.isStaff || false;
  const annualLeaveRemaining = leaveBalance?.balance?.annualLeave || 0;
  const currentYearAllocation = leaveBalance?.entitlement?.annualLeaveCurrentYear 
                              || leaveBalance?.entitlement?.annualLeaveTotal || 0;
  const annualLeaveUsed = leaveBalance?.entitlement?.annualLeaveUsed || 0;
  const tilHours = leaveBalance?.balance?.timeInLieu || 0;

  // Get next performance
  const nextPerformance = nextPerformances && nextPerformances.length > 0 ? nextPerformances[0] : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" gutterBottom>
            Welcome, {personnel.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your personal dashboard
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" icon={<User />} iconPosition="start" />
            <Tab label="Leave & TIL" icon={<BeachAccess />} iconPosition="start" disabled={!isStaff} />
            <Tab label="Upcoming Work" icon={<Calendar />} iconPosition="start" />
            <Tab label="Documents" icon={<FileText />} iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={currentTab} index={0}>
          {/* Overview Tab - Summary Cards */}
          <Grid container spacing={3}>
            {/* Profile Summary Card - ENHANCED */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  {/* Profile Photo */}
                  {(user as any)?.profileImage && (
                    <Box
                      component="img"
                      src={(user as any).profileImage}
                      alt={personnel.name}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <User sx={{ fontSize: 24 }} />
                      <Typography variant="h5">Your Profile</Typography>
                      
                      {/* System Role Badge */}
                      {user?.role && (
                        <Chip 
                          label={user.role.toUpperCase()} 
                          color={user.role === 'admin' ? 'error' : 'primary'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                      
                      {/* Artist Badge */}
                      {user?.isArtist && (
                        <Chip 
                          label="ARTIST" 
                          color="secondary"
                          size="small"
                        />
                      )}
                    </Box>
                    
                    <Grid container spacing={3}>
                      {/* Left Column - Contact */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Contact Information
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Email:</strong> {personnel.email}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Phone:</strong> {personnel.phone}
                        </Typography>
                        {personnel.address && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Address:</strong><br />
                            {personnel.address.street}<br />
                            {personnel.address.city}, {personnel.address.state} {personnel.address.postalCode}<br />
                            {personnel.address.country}
                          </Typography>
                        )}
                      </Grid>
                      
                      {/* Right Column - Employment & Experience */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Employment Details
                        </Typography>
                        {personnel.employmentStatus && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Status:</strong> {personnel.employmentStatus.charAt(0).toUpperCase() + personnel.employmentStatus.slice(1)}
                          </Typography>
                        )}
                        {personnel.employmentType && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Type:</strong> {personnel.employmentType.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </Typography>
                        )}
                        {personnel.startDate && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Start Date:</strong> {new Date(personnel.startDate).toLocaleDateString('en-IE', { month: 'long', year: 'numeric' })}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Experience:</strong> {personnel.experience} years
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Skill Level:</strong> {personnel.skillLevel}
                        </Typography>
                        
                        {/* Roles */}
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                          Roles
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {personnel.roles?.map((role, index) => (
                            <Chip 
                              key={index}
                              label={role}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Leave & TIL Summary Card - CLICKABLE */}
            {isStaff && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardActionArea onClick={() => setCurrentTab(1)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BeachAccess color="primary" />
                          <Typography variant="h6">Leave & Time In Lieu</Typography>
                        </Box>
                        <ArrowRight />
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      
                      {/* Annual Leave */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Annual Leave
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">
                          {annualLeaveRemaining} days
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {annualLeaveUsed} used / {currentYearAllocation} allocated
                        </Typography>
                      </Box>

                      {/* TIL */}
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Time in Lieu
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="info.main">
                          {tilHours}h
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Available to use
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="primary.main">
                          Click to view details →
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            )}

            {/* Next Performance Card - CLICKABLE */}
            <Grid item xs={12} md={isStaff ? 6 : 12}>
              <Card>
                <CardActionArea onClick={() => setCurrentTab(2)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Calendar sx={{ color: 'action.active' }} />
                        <Typography variant="h6">Next Assignment</Typography>
                      </Box>
                      <ArrowRight />
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {nextPerformance ? (
                      <Box>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          {nextPerformance.simpleBandName || 
                           (typeof nextPerformance.event === 'object' && nextPerformance.event !== null
                             ? nextPerformance.event.name 
                             : 'Untitled Event')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {typeof nextPerformance.venue === 'object' && nextPerformance.venue !== null
                            ? nextPerformance.venue.name
                            : 'Venue TBC'}
                        </Typography>
                        <Chip 
                          label={new Date(nextPerformance.startTime).toLocaleDateString('en-IE', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                          color="primary"
                          sx={{ mt: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          {new Date(nextPerformance.startTime).toLocaleTimeString('en-IE', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Calendar sx={{ fontSize: 48, opacity: 0.3 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          No upcoming assignments
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="primary.main">
                        Click to view all assignments →
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>

            {/* Documents Card - CLICKABLE */}
            <Grid item xs={12}>
              <Card>
                <CardActionArea onClick={() => setCurrentTab(3)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText sx={{ fontSize: 24 }} />
                        <Typography variant="h6">Your Documents</Typography>
                      </Box>
                      <ArrowRight />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Access your contracts, schedules, and other documents
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          {/* Leave & TIL Tab - Full MyLeaveAndTILPage content */}
          {isStaff ? (
            <MyLeaveAndTILContent personnelId={personnel._id} />
          ) : (
            <Alert severity="info">Leave management is only available for staff members.</Alert>
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          {/* Upcoming Work Tab */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Calendar sx={{ fontSize: 24 }} />
              <Typography variant="h5">Your Upcoming Assignments</Typography>
            </Box>
            
            {Array.isArray(nextPerformances) && nextPerformances.length > 0 ? (
              <UpcomingPerformances 
                performances={nextPerformances}
                personnelId={personnel._id}
              />
            ) : (
              <Alert severity="info">
                No upcoming performances assigned to you.
              </Alert>
            )}
          </Paper>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          {/* Documents Tab */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <FileText sx={{ fontSize: 24 }} />
              <Typography variant="h5">Your Documents</Typography>
            </Box>
            <Alert severity="info">
              Document management functionality coming soon...
            </Alert>
          </Paper>
        </TabPanel>
      </Container>
    </Box>
  );
};

export default PersonnelDashboard;